import { TvSource } from '@reliance/types/models/Tv';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import { useAlerts } from '../alert/AlertContext';
import api from '../common/api';
import { useTvSources } from '../hooks/Tv';
import type { TvSourceFilters } from '../hooks/Tv';
import { formatDate } from '../format/DateTime';
import NotesModal from '../note/NotesModal';
import { parseNotesJson } from '../note/parseNotes';
import BaseHtmlTable from '../table/BaseHtmlTable';
import { useServerTableFilterState } from '../table/useServerTableFilterState';
import AddEditModal from './AddEditModal';

export type TvSourceTableHandle = {
  refresh(): void;
  loading: boolean;
};

type TvSourceTableProps = {
  filters?: TvSourceFilters;
  hiddenColumns?: string[];
  showAddButton?: boolean;
  sortByFacility?: boolean;
  extraRowActions?: (source: TvSource, refresh: () => void) => React.ReactNode;
  onChanged?: () => void;
  onLoadingChange?: (loading: boolean) => void;
};

const columnHelper = createColumnHelper<TvSource>();

const ALL_COLUMNS = [
  { id: 'name' },
  { id: 'macAddress' },
  { id: 'localIpAddress' },
  { id: 'facility' },
  { id: 'enabled' },
  { id: 'telemetryStatus' },
  { id: 'deviceVersion' },
  { id: 'lastCheckIn' },
  { id: 'housingUnits' },
  { id: 'relayControlUrl' },
  { id: 'relayIngestUrl' },
  { id: 'actions' },
];

const healthBadgeBg = (status: string | null | undefined): string => {
  switch (status) {
    case 'ok':
      return 'success';
    case 'degraded':
      return 'warning';
    case 'critical':
      return 'danger';
    default:
      return 'secondary';
  }
};

const TvSourceTable = React.forwardRef<TvSourceTableHandle, TvSourceTableProps>((props, ref) => {
  const {
    filters: externalFilters,
    hiddenColumns = [],
    showAddButton = false,
    sortByFacility = false,
    extraRowActions,
    onChanged,
    onLoadingChange,
  } = props;

  const alerts = useAlerts();
  const hiddenColumnSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns]);

  const columns = useMemo(
    () => ALL_COLUMNS.filter((c) => !hiddenColumnSet.has(c.id)),
    [hiddenColumnSet]
  );

  const baseRecord = useMemo(() => ({ ...(externalFilters ?? {}) }), [externalFilters]);
  const tableState = useServerTableFilterState(columns, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: 1000 },
  });
  const filters = useMemo((): TvSourceFilters => {
    return {
      ...(tableState.mergedRecord as TvSourceFilters),
      ...(externalFilters ?? {}),
    };
  }, [tableState.mergedRecord, externalFilters]);

  const [showAddEdit, setShowAddEdit] = useState(false);
  const [editSource, setEditSource] = useState<TvSource | null>(null);
  const [notesSource, setNotesSource] = useState<TvSource | null>(null);

  const sources = useTvSources(filters);
  const result = sources.result;

  useEffect(() => {
    onLoadingChange?.(sources.loading);
  }, [sources.loading, onLoadingChange]);

  const refresh = useCallback(() => {
    void sources.execute();
    onChanged?.();
  }, [sources, onChanged]);

  // Expose refresh so a parent can reload this table
  useImperativeHandle(
    ref,
    () => ({
      refresh: () => {
        void sources.execute();
      },
      loading: sources.loading,
    }),
    [sources]
  );

  const handleAddNote = useCallback(
    async (draft: { note: string }) => {
      if (notesSource == null) {
        return;
      }
      try {
        await api.put(`/tv/source/${notesSource.id}`, { notes: draft.note });
        setNotesSource(null);
        refresh();
        alerts.success('Note was added');
      } catch {
        alerts.error('An error occurred while adding the note', true);
      }
    },
    [alerts, notesSource, refresh]
  );

  const notesModalTitle = useMemo(() => {
    if (notesSource == null) {
      return 'Notes';
    }
    const label = notesSource.name ?? notesSource.macAddress;
    return `Notes: ${label}`;
  }, [notesSource]);

  const rows = useMemo(() => {
    const data = result?.data ?? [];
    if (!sortByFacility) {
      return [...data].sort((a, b) =>
        (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' })
      );
    }
    return [...data].sort((a, b) => {
      const facilityA = a.facility?.name ?? '';
      const facilityB = b.facility?.name ?? '';
      const aHasFacility = a.facility?.id != null;
      const bHasFacility = b.facility?.id != null;

      if (aHasFacility !== bHasFacility) {
        return aHasFacility ? 1 : -1;
      }

      const facilityCmp = facilityA.localeCompare(facilityB, undefined, { sensitivity: 'base' });
      if (facilityCmp !== 0) {
        return facilityCmp;
      }

      return (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' });
    });
  }, [result?.data, sortByFacility]);

  const columnDefs = [
    columnHelper.accessor('name', {
      header: () => 'Name',
      cell: (info) => info.getValue() ?? '',
    }),
    columnHelper.accessor('macAddress', {
      header: () => 'MAC Address',
    }),
    columnHelper.accessor('localIpAddress', {
      header: () => 'Local IP Address',
      cell: (info) => info.getValue() ?? '',
    }),
    ...(!hiddenColumnSet.has('facility')
      ? [
          columnHelper.display({
            id: 'facility',
            header: () => 'Facility',
            cell: ({ row }) => {
              const facility = row.original.facility;
              if (!facility?.id) return '';
              return (
                <Link to={`/facilities/view/${facility.id}`}>
                  {facility.name ?? `Facility ${facility.id} (${facility.state})`}
                </Link>
              );
            },
          }),
        ]
      : []),
    columnHelper.accessor('enabled', {
      header: () => 'Enabled',
      cell: (info) => (info.getValue() ? 'Yes' : 'No'),
    }),
    columnHelper.accessor('telemetryStatus', {
      header: () => 'Health',
      cell: (info) => {
        const status = info.getValue();
        if (status == null || status === '') {
          return '—';
        }
        return (
          <Badge bg={healthBadgeBg(status)} text={status === 'degraded' ? 'dark' : undefined}>
            {status}
          </Badge>
        );
      },
    }),
    columnHelper.accessor('deviceVersion', {
      header: () => 'Version',
      cell: (info) => info.getValue() ?? '',
    }),
    columnHelper.accessor('lastCheckIn', {
      header: () => 'Last Check-In',
      cell: (info) => formatDate(info.getValue(), true),
    }),
    ...(!hiddenColumnSet.has('housingUnits')
      ? [
          columnHelper.display({
            id: 'housingUnits',
            header: () => 'Housing Units',
            cell: ({ row }) => {
              const units = row.original.housingUnits;
              if (!units?.length) return '';
              const names = units.map((u) => u.name ?? u.id).filter(Boolean);
              return names.length ? names.join(', ') : '';
            },
          }),
        ]
      : []),
    ...(!hiddenColumnSet.has('relayControlUrl')
      ? [
          columnHelper.accessor('relayControlUrl', {
            header: () => 'Relay Control URL',
            cell: (info) => info.getValue() ?? '',
          }),
        ]
      : []),
    ...(!hiddenColumnSet.has('relayIngestUrl')
      ? [
          columnHelper.accessor('relayIngestUrl', {
            header: () => 'Relay Ingest URL',
            cell: (info) => info.getValue() ?? '',
          }),
        ]
      : []),
    columnHelper.display({
      id: 'actions',
      header: () => 'Actions',
      meta: { cellClassName: 'text-nowrap' },
      cell: (data) => {
        const source = data.row.original;
        const noteCount = parseNotesJson(source.notes).length;
        return (
          <div className="d-flex flex-nowrap align-items-center gap-1">
            {extraRowActions?.(source, refresh)}
            <Button
              size="sm"
              variant="primary"
              className="text-nowrap"
              onClick={() => {
                setEditSource(source);
                setShowAddEdit(true);
              }}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="text-nowrap"
              title="Notes"
              onClick={() => setNotesSource(source)}>
              Notes{noteCount > 0 ? ` (${noteCount})` : ''}
            </Button>
          </div>
        );
      },
    }),
  ];

  return (
    <>
      {showAddButton && (
        <Button
          variant="primary"
          className="mb-3"
          onClick={() => {
            setEditSource(null);
            setShowAddEdit(true);
          }}>
          Add TV Stream
        </Button>
      )}

      <BaseHtmlTable
        columnDefs={columnDefs}
        rows={rows}
        loading={sources.loading}
        paginationMode="server"
        page={result?.page}
        totalPages={result?.totalPages}
        total={result?.total}
        perPage={result?.perPage ?? tableState.pagination.pageSize}
        serverPagination={tableState.serverPagination}
        onServerPaginationChange={tableState.onServerPaginationChange}
        serverSorting={tableState.serverSorting}
        onServerSortingChange={tableState.onServerSortingChange}
        serverColumnFilters={tableState.serverColumnFilters}
        onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
      />

      {showAddEdit && (
        <AddEditModal
          source={editSource}
          onClose={(saved) => {
            setShowAddEdit(false);
            setEditSource(null);
            if (saved) {
              refresh();
            }
          }}
        />
      )}

      <NotesModal
        show={notesSource != null}
        title={notesModalTitle}
        notesJson={notesSource?.notes}
        onHide={() => setNotesSource(null)}
        onAdd={handleAddNote}
      />
    </>
  );
});

TvSourceTable.displayName = 'TvSourceTable';

export default TvSourceTable;
